import * as React from 'react';
const { useMemo, useCallback } = React;
import { keyBy } from 'lodash';
import { makeStyles } from '@material-ui/core/styles';
import Select, { SelectProps } from '@material-ui/core/Select';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormControl from '@material-ui/core/FormControl';

import { InputStandartMargins, TextFieldPlaceholder } from '../text-field';
import IconButton from '../icon-button';
import Icon from '../icon';
import { declOfNum } from '../../utils';
import { defaultTheme, WithMaterialThemeProps } from '../../styles';
import {
	Root,
	IconButtonLayout,
} from './styled';


const getSourceValue = (item: unknown, mapper: DataSourceMapperType) => mapper.getSourceValue(item);
const getSourceText = (item: unknown, mapper: DataSourceMapperType) => mapper.getSourceText(item);

const generateDataSourceByMapper = (items: Array<unknown>, mapper: DataSourceMapperType) => 
	items.map(item => ({ value: getSourceValue(item, mapper), name: getSourceText(item, mapper), origin: item }));

type Value = string | number

type DataSourceItemType = {
	value: Value;
	name: string;
	origin: unknown;
}

type DataSourceMapperType = {
	getSourceValue: (item: unknown) => string | number;
	getSourceText: (item: unknown) => string;
}

export type SelectFieldProps = {
	value: Value | Array<Value> | Array<unknown>;
	label: React.ReactNode;
	dataSource: Array<any>;
	dataSourceMapper?: DataSourceMapperType; //необходим, чтобы преобразовать элемент dataSource в {value, name}
	readonly?: boolean;
	error?: boolean;
	helperText?: React.ReactNode;
	margin?: InputStandartMargins;
	placeholder?: string;
	itemsMsgs?: [string, string, string];
	getValue?: (item: unknown) => Value; //нужно если в value передается полный объект, а не идентификатор
	renderItem?: (item: unknown, name: string) => React.ReactNode;
	renderValue?: (item: unknown, name: string) => React.ReactNode;
	onClear?: () => void;
	onChange: (e: React.SyntheticEvent, value: Value | Array<Value>, item: DataSourceItemType | Array<DataSourceItemType>) => void;
} & Pick<SelectProps,
	'tabIndex'
	| 'autoWidth'
	| 'multiple'
	| 'onClose'
	| 'onOpen'
	| 'open'
	| 'disabled'
	| 'fullWidth'
	| 'name'
	| 'id'>

const useStyles = makeStyles((mt: WithMaterialThemeProps) => {
	const { customTheme } = mt;
	const theme = customTheme || defaultTheme; 

	return {
		menuItem: {
			minHeight: 24,
			'&.Mui-selected': {
				color: theme.color.accentPrimary,
				backgroundColor: 'transparent'
			}
		}
	}
});

const SelectField: React.SFC<SelectFieldProps> = (props) => {
	const {
		id,
		name,
		value,
		label,
		dataSource,
		dataSourceMapper: mapper,
		getValue,
		disabled,
		readonly,
		fullWidth,
		error,
		helperText,
		margin,
		placeholder,
		multiple,
		itemsMsgs,
		renderItem: itemRenderer,
		renderValue: valueRenderer,
		onClear,
		onChange,
		...rest
	} = props;
	const classes = useStyles({});
	const dataSourceMap = useMemo(() => keyBy(dataSource, s => getSourceValue(s, mapper)), [dataSource]);
	const handleChange = useCallback((e: any) => {
		const isValueExists = multiple ? Boolean(value[0]) : value;
		const isArrayTargetValue = Array.isArray(e.target.value);
		const calcValue = isArrayTargetValue
			?	e.target.value.filter(Boolean)
			: e.target.value;
		const item = isArrayTargetValue
			?	calcValue.map(v => dataSourceMap[v])
			: dataSourceMap[calcValue];
		const processedValue = multiple && isValueExists
			?	(typeof value[0] === 'number' || typeof value[0] === 'string')
				? calcValue
				: item 
			: calcValue;

		onChange(e, processedValue, item);
	}, [dataSourceMap]);

	const getDefaultValue = useMemo(() => () => (multiple ? [0] : 0), [multiple]);

	const renderItem = (source: DataSourceItemType, idx: number) => {
		return (
			<MenuItem
				key={idx}
				value={source.value}
				className={classes.menuItem}>
				{typeof itemRenderer === 'function' ? itemRenderer(source.origin, source.name) : source.name}
			</MenuItem>
		);
	};

	const renderValue = (value: string | number) => valueRenderer(dataSourceMap[value], name);

	const renderValueDefault = (value: Value | Array<string>) => {
		const names = Array.isArray(value)
			? value.map(v => v ? mapper.getSourceText(dataSourceMap[v]) : v)
			: dataSourceMap[value] ? [mapper.getSourceText(dataSourceMap[value])] : null;

		if (!names || !names[0]) return <TextFieldPlaceholder>{placeholder}</TextFieldPlaceholder>;

		const str = names.length > 1
			? `Выбрано ${names.length} ${declOfNum(names.length, itemsMsgs)}`
			: names[0];

		return str;
	};

	const getPrecessedValue = useMemo(() => () => {
		const calcValue = Array.isArray(value)
			? (value as Array<any>).map((v) => getValue(v))
			: getValue(value);

		return Array.isArray(calcValue)
			?	calcValue.length === 0
				?	getDefaultValue()
				: calcValue
			: calcValue || getDefaultValue()
	}, [value]);

	return (
		<Root
			disabled={disabled}
			readonly={readonly}
			fullWidth={fullWidth}
			error={error}
			margin={margin}
			withClearBtn={typeof onClear === 'function'}>
			<FormControl fullWidth={fullWidth} error={error}>
			<InputLabel htmlFor={id} shrink>{label}</InputLabel>
				<Select
					{...rest}
					value={getPrecessedValue()}
					multiple={multiple}
					variant='standard'
					disabled={disabled || readonly}
					fullWidth={fullWidth}
					MenuProps={{
						getContentAnchorEl: null,
						anchorOrigin: {
							vertical: 'bottom',
							horizontal: 'left'
						},
						PaperProps: {
							square: true,
							elevation: 3
						}
					}}
					inputProps={{
						name,
						id,
					}}
					renderValue={
						typeof valueRenderer === 'function'
							? renderValue
							: typeof placeholder !== 'undefined'
								? renderValueDefault
								: undefined
					}
					onChange={handleChange}>
					{generateDataSourceByMapper(dataSource, mapper).map(renderItem)}
				</Select>
				{helperText && <FormHelperText>{helperText}</FormHelperText>}
			</FormControl>
			{
				typeof onClear === 'function' &&
				<IconButtonLayout>
					<IconButton onClick={onClear}>
						<Icon type='clear' width={14} height={14} />
					</IconButton>
				</IconButtonLayout>
			}
		</Root>
	);
}

SelectField.displayName = 'SelectField';
SelectField.defaultProps = {
	getValue: (item: any) => item,
	dataSourceMapper: {
		getSourceValue: (item: DataSourceItemType) => item.value,
		getSourceText: (item: DataSourceItemType) => item.name
	},
	margin: 'bottom-small',
	itemsMsgs: ['элемент', 'элемента', 'элементов']
};

export default SelectField;
